import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { CATEGORIES, POLL_TAG, RELAYS, US_STATES } from '../../utils/constants.js'
import { POLL_KIND } from '../../utils/nostr.js'
import { jurisdictionTagValue } from '../../utils/jurisdiction.js'

const DURATIONS = [
  { d: 3,  label: '3 days' },
  { d: 7,  label: '1 week' },
  { d: 14, label: '2 weeks' },
  { d: 30, label: '1 month' },
]

// Persist form state across the sign-in modal round trip. Per-tab
// (sessionStorage) so different tabs don't fight over the same draft;
// cleared on successful post.
const PERSIST_KEY = 'verdant_propose_draft'

function loadPersisted() {
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

// Phase 1 spam-control bridge: 1 poll per pubkey per 24h.
// Trivially bypassable client-side, but slows naive flooding until
// the Phase 3 NIP-57 zap fee ships (SPEC §16).
const RATE_LIMIT_KEY = 'verdant_last_posts'
const RATE_LIMIT_MS = 24 * 60 * 60 * 1000

function lastPostAt(pubkey) {
  if (!pubkey) return 0
  try {
    const map = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || '{}')
    return map[pubkey] || 0
  } catch { return 0 }
}

function setLastPostAt(pubkey, ts) {
  if (!pubkey) return
  try {
    const map = JSON.parse(localStorage.getItem(RATE_LIMIT_KEY) || '{}')
    map[pubkey] = ts
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(map))
  } catch { /* noop */ }
}

function formatRemaining(ms) {
  const hours = Math.floor(ms / 3600000)
  const minutes = Math.ceil((ms % 3600000) / 60000)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

export function ProposePane({ isLoggedIn, pubkey, sign, publish, jurisdiction, onPosted, onSignIn }) {
  const persisted = loadPersisted() || {}
  const [pollType, setPollType] = useState(persisted.pollType || 'bill')
  const [level, setLevel] = useState(persisted.level || 'federal')
  const [stateCode, setStateCode] = useState(persisted.stateCode ?? (jurisdiction?.state || ''))
  const [city, setCity] = useState(persisted.city ?? (jurisdiction?.city || ''))
  const [billId, setBillId] = useState(persisted.billId || '')
  const [sourceUrl, setSourceUrl] = useState(persisted.sourceUrl || '')
  const [title, setTitle] = useState(persisted.title || '')
  const [summary, setSummary] = useState(persisted.summary || '')
  const [category, setCategory] = useState(persisted.category || CATEGORIES[0])
  const [raceName, setRaceName] = useState(persisted.raceName || '')
  const [office, setOffice] = useState(persisted.office || '')
  const [electionDate, setElectionDate] = useState(persisted.electionDate || '')
  const [candidates, setCandidates] = useState(persisted.candidates || [
    { name: '', party: '' },
    { name: '', party: '' },
  ])
  const [duration, setDuration] = useState(persisted.duration ?? 7)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(null)
  const [autofilling, setAutofilling] = useState(false)
  const [autofillNote, setAutofillNote] = useState(null)
  // Separate source URL field for the candidate-race flow — bills already
  // use `sourceUrl` above; reusing it would conflate two purposes.
  const [raceSourceUrl, setRaceSourceUrl] = useState(persisted.raceSourceUrl || '')

  useEffect(() => {
    try {
      sessionStorage.setItem(PERSIST_KEY, JSON.stringify({
        pollType, level, stateCode, city, billId, sourceUrl, title, summary,
        category, raceName, office, electionDate, candidates, duration, raceSourceUrl,
      }))
    } catch { /* noop */ }
  }, [pollType, level, stateCode, city, billId, sourceUrl, title, summary,
      category, raceName, office, electionDate, candidates, duration, raceSourceUrl])

  const handleAutofillFromLink = async () => {
    const url = sourceUrl.trim()
    if (!url) {
      setAutofillNote({ type: 'warn', text: 'Paste a source URL above first.' })
      return
    }
    setAutofilling(true)
    setAutofillNote(null)
    try {
      const r = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        throw new Error(data?.error || `server returned ${r.status}`)
      }
      const filled = []
      if (data.title) {
        setTitle(data.title)
        filled.push('title')
      }
      if (data.summary) {
        setSummary(data.summary)
        filled.push('summary')
      }
      if (data.bill_id) {
        setBillId(data.bill_id)
        filled.push('bill ID')
      }
      if (data.level && ['federal', 'state', 'city'].includes(data.level)) {
        setLevel(data.level)
        filled.push('level')
      }
      if (data.state && /^[A-Z]{2}$/.test(data.state)) {
        setStateCode(data.state)
        filled.push('state')
      }
      if (data.city) {
        setCity(data.city)
        filled.push('city')
      }
      // Only accept categories that match our canonical list — Claude
      // sometimes paraphrases instead of choosing one of the 32 options.
      if (data.category && CATEGORIES.includes(data.category)) {
        setCategory(data.category)
        filled.push('topic')
      }

      if (!filled.length) {
        setAutofillNote({
          type: 'warn',
          text: 'No bill content recognized at that URL — fill in manually.',
        })
        return
      }
      setAutofillNote({
        type: 'ok',
        text: `Filled ${filled.join(', ')} from the source. Review and edit before submitting.`,
      })
    } catch (err) {
      Sentry.withScope((s) => {
        s.setTag('op', 'autofill')
        s.setExtra('kind', 'bill')
        s.setExtra('sourceUrl', url)
        Sentry.captureException(err)
      })
      setAutofillNote({
        type: 'warn',
        text: 'Autofill failed: ' + (err?.message || 'unknown error'),
      })
    } finally {
      setAutofilling(false)
    }
  }

  const handleAutofillRaceFromLink = async () => {
    const url = raceSourceUrl.trim()
    if (!url) {
      setAutofillNote({ type: 'warn', text: 'Paste a source URL above first.' })
      return
    }
    setAutofilling(true)
    setAutofillNote(null)
    try {
      const r = await fetch('/api/generate-race', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        throw new Error(data?.error || `server returned ${r.status}`)
      }

      const filled = []
      if (data.race_name) {
        setRaceName(data.race_name)
        filled.push('race name')
      }
      if (data.office) {
        setOffice(data.office)
        filled.push('office')
      }
      if (data.level && ['federal', 'state', 'city'].includes(data.level)) {
        setLevel(data.level)
        filled.push('level')
      }
      if (data.state && /^[A-Z]{2}$/.test(data.state)) {
        setStateCode(data.state)
        filled.push('state')
      }
      if (data.city) {
        setCity(data.city)
        filled.push('city')
      }
      if (data.election_date && /^\d{4}-\d{2}-\d{2}$/.test(data.election_date)) {
        setElectionDate(data.election_date)
        filled.push('election date')
      }
      if (Array.isArray(data.candidates) && data.candidates.length >= 2) {
        setCandidates(data.candidates.map((c) => ({
          name:  c.name  || '',
          party: c.party || '',
        })))
        filled.push(`${data.candidates.length} candidates`)
      }

      if (!filled.length) {
        setAutofillNote({
          type: 'warn',
          text: 'No race content recognized at that URL — fill in manually.',
        })
        return
      }
      setAutofillNote({
        type: 'ok',
        text: `Filled ${filled.join(', ')} from the source. Review and edit before submitting.`,
      })
    } catch (err) {
      Sentry.withScope((s) => {
        s.setTag('op', 'autofill')
        s.setExtra('kind', 'race')
        s.setExtra('sourceUrl', url)
        Sentry.captureException(err)
      })
      setAutofillNote({
        type: 'warn',
        text: 'Autofill failed: ' + (err?.message || 'unknown error'),
      })
    } finally {
      setAutofilling(false)
    }
  }

  const updateCandidate = (i, key, value) => {
    setCandidates((prev) =>
      prev.map((c, idx) => (idx === i ? { ...c, [key]: value } : c))
    )
  }
  const addCandidate = () =>
    setCandidates((prev) => [...prev, { name: '', party: '' }])
  const removeCandidate = (i) =>
    setCandidates((prev) => prev.filter((_, idx) => idx !== i))

  const buildJurisdictionTag = () => {
    if (level === 'federal') return 'US'
    if (level === 'state' && stateCode) return `US-${stateCode}`
    if (level === 'city' && stateCode && city) return `US-${stateCode}-${city}`
    return jurisdictionTagValue(jurisdiction) || 'US'
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isLoggedIn) {
      setMessage({ type: 'error', text: 'Sign in first.' })
      return
    }
    const last = lastPostAt(pubkey)
    const elapsed = Date.now() - last
    if (last && elapsed < RATE_LIMIT_MS) {
      setMessage({
        type: 'error',
        text: `Rate limit: 1 poll per 24h. Try again in ${formatRemaining(RATE_LIMIT_MS - elapsed)}.`,
      })
      return
    }
    setSubmitting(true)
    setMessage(null)

    try {
      const endsAt = Math.floor(Date.now() / 1000) + duration * 86400
      const jurisdictionTag = buildJurisdictionTag()
      const tags = [
        ['t', POLL_TAG],
        ['endsAt', String(endsAt)],
        // NIP-88: lets Primal and other poll clients render and count it.
        ['polltype', 'singlechoice'],
        ...RELAYS.map((r) => ['relay', r]),
        ['level', level === 'federal' || level === 'state' || level === 'city' ? level : 'user'],
        ['jurisdiction', jurisdictionTag],
      ]

      let content
      if (pollType === 'bill') {
        if (!summary.trim()) throw new Error('Plain summary is required.')
        tags.push(['option', '0', '✅ Yes'])
        tags.push(['option', '1', '❌ No'])
        tags.push(['option', '2', '⬜ Unsure'])
        tags.push(['plain_summary', summary.trim()])
        tags.push(['category', category])
        if (billId.trim()) tags.push(['t', billId.trim().toUpperCase()])
        if (sourceUrl.trim()) tags.push(['r', sourceUrl.trim()])
        content = [
          `🗳️ VOTE: ${title || summary}`,
          '',
          summary,
          sourceUrl ? `\n${sourceUrl}` : '',
          `\n#${POLL_TAG}${billId ? ` #${billId.toUpperCase()}` : ''} #Verdant`,
        ].filter(Boolean).join('\n')
      } else {
        if (!raceName.trim()) throw new Error('Race name is required.')
        if (candidates.filter((c) => c.name.trim()).length < 2) {
          throw new Error('Add at least two candidates.')
        }
        tags.push(['type', 'candidate'])
        const raceId = raceName.trim().toUpperCase().replace(/\s+/g, '-')
        tags.push(['race', raceId])
        if (office) tags.push(['office', office])
        if (electionDate) tags.push(['election_date', electionDate])
        let optionIndex = 0
        for (const c of candidates) {
          if (!c.name.trim()) continue
          tags.push(['candidate', c.name.trim(), c.party.trim() || 'Independent'])
          tags.push(['option', String(optionIndex++), c.name.trim()])
        }
        content = [
          `🗳️ RACE: ${raceName}`,
          '',
          office ? `Who should be the next ${office}?` : 'Cast your vote.',
          `\n#${POLL_TAG} #${raceId} #Verdant`,
        ].join('\n')
      }

      const template = {
        kind: POLL_KIND,
        created_at: Math.floor(Date.now() / 1000),
        content,
        tags,
      }
      let signed
      try {
        signed = await sign(template)
      } catch (err) {
        throw new Error(`Signing failed: ${err?.message || 'rejected by extension'}`)
      }

      const result = await publish(signed)
      console.info('[Verdant] published poll', {
        id: signed.id,
        accepted: result.accepted,
        total: result.total,
        tags: signed.tags,
      })
      setMessage({
        type: 'success',
        text: `Posted to ${result.accepted}/${result.total} relays.`,
      })
      try { sessionStorage.removeItem(PERSIST_KEY) } catch { /* noop */ }
      setLastPostAt(pubkey, Date.now())
      onPosted?.(signed, result)
    } catch (err) {
      console.error('[Verdant] publish failed', err)
      setMessage({
        type: 'error',
        text: err?.message || 'Failed to post.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="pane-card">
      <h2>Propose a poll</h2>
      <p className="card-summary" style={{ marginTop: 4, marginBottom: 16 }}>
        Post a bill or candidate race for the community to vote on.
      </p>

      {!isLoggedIn && (
        <div className="propose-signin-banner" role="note">
          <span>Sign in to post — your draft is saved.</span>
          {onSignIn && (
            <button type="button" className="btn btn-primary" onClick={onSignIn}>
              Sign in
            </button>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="field-row">
          <div className="field">
            <label>Poll type</label>
            <select value={pollType} onChange={(e) => setPollType(e.target.value)}>
              <option value="bill">Bill or resolution</option>
              <option value="candidate">Candidate race</option>
            </select>
          </div>
          <div className="field">
            <label>Level of government</label>
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="federal">Federal</option>
              <option value="state">State</option>
              <option value="city">City</option>
            </select>
          </div>
        </div>

        {(level === 'state' || level === 'city') && (
          <div className="field-row">
            <div className="field">
              <label>State</label>
              <select value={stateCode} onChange={(e) => setStateCode(e.target.value)} required>
                <option value="">Select state…</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
            {level === 'city' && (
              <div className="field">
                <label>City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g. Seattle"
                  required
                />
              </div>
            )}
          </div>
        )}

        {pollType === 'bill' ? (
          <>
            <div className="field-row">
              <div className="field">
                <label>Bill identifier</label>
                <input
                  type="text"
                  value={billId}
                  onChange={(e) => setBillId(e.target.value)}
                  placeholder="HR6387 (optional for state/local)"
                />
              </div>
              <div className="field">
                <label>Source URL</label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://www.congress.gov/…"
                />
              </div>
            </div>
            <div className="field">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleAutofillFromLink}
                disabled={autofilling || !sourceUrl.trim()}
              >
                {autofilling ? 'Reading source…' : '✨ Generate title & summary from link'}
              </button>
              {autofillNote && (
                <div
                  className={autofillNote.type === 'warn' ? 'error-state' : 'note'}
                  style={{ marginTop: 8 }}
                >
                  {autofillNote.text}
                </div>
              )}
            </div>
            <div className="field">
              <label>Title (optional)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="FIRE Act — wildfire prevention funding"
              />
            </div>
            <div className="field">
              <label>Plain-English summary</label>
              <textarea
                rows={3}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="One sentence anyone can understand."
                required
              />
            </div>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label>Source URL (optional)</label>
              <input
                type="url"
                value={raceSourceUrl}
                onChange={(e) => setRaceSourceUrl(e.target.value)}
                placeholder="https://ballotpedia.org/… or news article about the race"
              />
            </div>
            <div className="field">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleAutofillRaceFromLink}
                disabled={autofilling || !raceSourceUrl.trim()}
              >
                {autofilling ? 'Reading source…' : '✨ Generate race details from link'}
              </button>
              {autofillNote && (
                <div
                  className={autofillNote.type === 'warn' ? 'error-state' : 'note'}
                  style={{ marginTop: 8 }}
                >
                  {autofillNote.text}
                </div>
              )}
            </div>
            <div className="field-row">
              <div className="field">
                <label>Race name</label>
                <input
                  type="text"
                  value={raceName}
                  onChange={(e) => setRaceName(e.target.value)}
                  placeholder="Seattle Mayoral Race 2026"
                  required
                />
              </div>
              <div className="field">
                <label>Office</label>
                <input
                  type="text"
                  value={office}
                  onChange={(e) => setOffice(e.target.value)}
                  placeholder="mayor"
                />
              </div>
            </div>
            <div className="field">
              <label>Election date</label>
              <input
                type="date"
                value={electionDate}
                onChange={(e) => setElectionDate(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Candidates</label>
              {candidates.map((c, i) => (
                <div key={i} className="field-row" style={{ marginBottom: 6 }}>
                  <input
                    type="text"
                    placeholder="Candidate name"
                    value={c.name}
                    onChange={(e) => updateCandidate(i, 'name', e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="text"
                      placeholder="Party"
                      value={c.party}
                      onChange={(e) => updateCandidate(i, 'party', e.target.value)}
                    />
                    {candidates.length > 2 && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => removeCandidate(i)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-ghost" onClick={addCandidate}>
                + Add candidate
              </button>
            </div>
          </>
        )}

        <div className="field">
          <label>Poll duration</label>
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
          >
            {DURATIONS.map((d) => (
              <option key={d.d} value={d.d}>{d.label}</option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={!isLoggedIn || submitting}
        >
          {submitting
            ? 'Posting…'
            : isLoggedIn
              ? 'Post poll'
              : 'Sign in to post'}
        </button>

        {message && (
          <div
            className={message.type === 'error' ? 'error-state' : 'note'}
            style={{ marginTop: 12 }}
          >
            {message.text}
          </div>
        )}

        <div className="note">
          Bills validated against congress.gov. Candidate races reviewed within
          24 hours.
        </div>
      </form>
    </section>
  )
}
